import { useEffect, useState } from "react";

export interface ToastMessage {
  id: number;
  text: string;
  tone?: "info" | "warn" | "error";
  /** Auto-dismiss in milliseconds. Defaults to 2800. */
  durationMs?: number;
  /** Optional link rendered after the text (e.g. a Vault note URL). */
  link?: {
    href: string;
    label: string;
  };
}

export interface ToastOptions {
  tone?: ToastMessage["tone"];
  durationMs?: number;
  link?: ToastMessage["link"];
}

const DEFAULT_DURATION_MS = 2800;

/** Very small toast stack — top-right, auto-dismiss. */
export function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: ToastMessage[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-16 z-40 flex flex-col items-center gap-2 px-4"
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastMessage;
  onDismiss: (id: number) => void;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setVisible(true);
    const t = setTimeout(
      () => onDismiss(toast.id),
      toast.durationMs ?? DEFAULT_DURATION_MS,
    );
    return () => clearTimeout(t);
  }, [toast.id, toast.durationMs, onDismiss]);

  const tone = toast.tone ?? "info";
  const toneClass =
    tone === "error"
      ? "bg-ink text-cream"
      : tone === "warn"
        ? "bg-rust text-cream"
        : "bg-cream-soft text-ink";

  return (
    <div
      className={[
        "pointer-events-auto flex max-w-[90vw] items-center gap-2 rounded-box border border-ink/20 px-3 py-2",
        "font-mono text-[11px] tracking-wider shadow-md",
        "transition-opacity duration-200",
        toneClass,
        visible ? "opacity-100" : "opacity-0",
      ].join(" ")}
    >
      <span className="truncate">{toast.text}</span>
      {toast.link && (
        <a
          href={toast.link.href}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 underline underline-offset-2"
        >
          {toast.link.label}
        </a>
      )}
    </div>
  );
}

/** Hook: returns [toasts, push, dismiss]. */
export function useToasts() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const push = (text: string, opts: ToastOptions | ToastMessage["tone"] = {}) => {
    // Back-compat: second arg was previously just a `tone` string.
    const normalised: ToastOptions =
      typeof opts === "string" ? { tone: opts } : opts;
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        text,
        ...normalised,
      },
    ]);
  };
  const dismiss = (id: number) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));
  return { toasts, push, dismiss } as const;
}
